export const DEFAULT_WIDGETS_STYLE = {
	fill: 'fit-box',
	position: 'center',
	textLabel: {
		text: 'Type something',
		font: 'Poppins',
		fontSize: '12',
		fontWeight: 'normal',
		color: '#126cfc',
		backgroundColor: 'transparent',
		hoverFontWeight: 'normal',
		hoverColor: '#126cfc',
		hoverBackgroundColor: 'transparent',
	},
	backgroundSettings: {
		backgroundColor: 'transparent',
		hoverBackgroundColor: 'transparent',
	},
	transformSettings: {
		borderRadiusLeft: '0',
		borderRadiusTop: '0',
		borderRadiusRight: '0',
		borderRadiusBottom: '0',
		radiusTop: '0',
		radiusBottom: '0',
		radiusRight: '0',
		radiusLeft: '0',
		paddingTop: '0',
		paddingBottom: '0',
		paddingRight: '0',
		paddingLeft: '0',
		hoverRadiusTop: '0',
		hoverRadiusBottom: '0',
		hoverRadiusRight: '0',
		hoverRadiusLeft: '0',
		hoverPaddingTop: '0',
		hoverPaddingBottom: '0',
		hoverPaddingRight: '0',
		hoverPaddingLeft: '0',
	},
	dimension: { w: 150, h: 100 },
	coordinate: { x: 0, y: 0 },
};

// The Interact editor's font dropdown offers a 'Default' option meaning "use the player's
// own font" (qbrick-ui `fonts` in constants/playerConfigurationsConstant.js). Express it as
// inheritance: the widget's ancestors already carry the configured player font, injected at
// runtime as `sheetPlayerFontPlayerStyling` (see util/util.js).
// NOTE: `default` is a RESERVED keyword in font-family, so even an older player that
// interpolates it straight into `${font}, Arial, sans-serif` produces an invalid
// declaration that gets dropped - which also lands on inheritance. This just makes it
// explicit rather than accidental.
export const DEFAULT_FONT = 'Default';

export const getFontFamily = (font) => (font === DEFAULT_FONT ? 'inherit' : `${font}, Arial, sans-serif`);

export const INTERACT_ELEMENT_ACTIONS = {
	openUrl: 'openUrl',
	jumpToTime: 'jumpToTime',
	loadVideo: 'loadVideo',
	videoToPlaylist: 'videoToPlaylist',
	playPause: 'playPause',
	play: 'play',
	pause: 'pause',
	customEvent: 'customEvent',
	fadeOut: 'fadeOut',
};

export const WIDGET_ACTIONS = {
	clickActions: {
		type: 'onClick',
		pause: 'pause',
		play: 'play',
		goToScene: 'goToScene',
		goToUrl: 'goToUrl',
		jumpToTime: 'jumpToTime',
	},
	entryActions: {
		type: 'onEntry',
		pause: 'pause',
		play: 'play',
		jumpToTime: 'jumpToTime',
	},
	exitActions: {
		type: 'onExit',
		pause: 'pause',
		play: 'play',
		jumpToTime: 'jumpToTime',
	},
};

export const getStyle = (checkObject, style, result) => {
	Object.keys(checkObject).forEach((key) => {
		if (checkObject[key] === undefined) {
			return result;
		}

		if (typeof checkObject[key] === 'string' || typeof checkObject[key] === 'number') {
			result[key] = style[key] ?? checkObject[key];
		} else {
			result[key] = style[key] !== undefined ? getStyle(checkObject[key], style[key], {}) : checkObject[key];
		}
	});
	return result;
};

// Background opacity (stored 0-100) as a 0-1 number, defaulting to fully opaque.
// MUST be applied on an SVG <g> (group opacity), NEVER on the foreignObject's HTML
// content: a CSS `opacity` on HTML inside a foreignObject promotes it to a compositing
// layer that Safari/WebKit displaces or drops (renders at the wrong position/size, or
// invisible) - Chrome composites it correctly, so it only breaks on Safari. Apply it on
// a NON-animated ancestor <g> (not the animated shape <g>, whose opacity fade/zoom
// keyframes would clobber it).
export const getWidgetOpacity = (backgroundSettings, isHover) => {
	const { backgroundOpacity, hoverBackgroundOpacity } = backgroundSettings || {};
	const raw = isHover ? (hoverBackgroundOpacity ?? backgroundOpacity) : backgroundOpacity;
	if (raw === undefined || raw === null || raw === '') { return 1; }
	const n = Number(raw);
	return Number.isNaN(n) ? 1 : n / 100;
};

export const getDropShadowStyle = (style) => {
	const { dropShadowX, dropShadowY, dropShadowBlur, dropShadowColor, dropShadowOpacity, hoverDropShadowX, hoverDropShadowY, hoverDropShadowBlur, hoverDropShadowColor, hoverDropShadowOpacity } = style;

	// box-shadow
	const dropShadow = `${dropShadowX}px ${dropShadowY}px${dropShadowBlur ? ` ${dropShadowBlur}px` : ''}${dropShadowColor ? ` rgba(${parseInt(dropShadowColor.slice(1, 3), 16)}, ${parseInt(dropShadowColor.slice(3, 5), 16)}, ${parseInt(dropShadowColor.slice(5, 7), 16)}, ${dropShadowOpacity ?? 1})` : ''}`.trim();
	const hoverDropShadow = `${hoverDropShadowX ?? dropShadowX}px ${hoverDropShadowY ?? dropShadowY}px${(hoverDropShadowBlur ?? dropShadowBlur) ? ` ${hoverDropShadowBlur ?? dropShadowBlur}px` : ''}${(hoverDropShadowColor ?? dropShadowColor) ? ` rgba(${parseInt((hoverDropShadowColor ?? dropShadowColor).slice(1, 3), 16)}, ${parseInt((hoverDropShadowColor ?? dropShadowColor).slice(3, 5), 16)}, ${parseInt((hoverDropShadowColor ?? dropShadowColor).slice(5, 7), 16)}, ${hoverDropShadowOpacity ?? dropShadowOpacity ?? 1})` : ''}`.trim();
	return {
		dropShadow: dropShadow || 'none',
		hoverDropShadow: hoverDropShadow,
	};
}

export const getBorderStyle = (style) => {
	const { borderWidth, borderType, borderColor, borderOpacity, hoverBorderWidth, hoverBorderType, hoverBorderColor, hoverBorderOpacity } = style;
	const borderColorWithOpacity = borderColor && borderColor !== 'transparent'
		? `rgba(${parseInt(borderColor.slice(1, 3), 16)}, ${parseInt(borderColor.slice(3, 5), 16)}, ${parseInt(
				borderColor.slice(5, 7),
				16,
		  )}, ${borderOpacity ?? 1})`
		: borderColor;
	const hoverBorderColorWithOpacity = hoverBorderColor && hoverBorderColor !== 'transparent'
		? `rgba(${parseInt(hoverBorderColor.slice(1, 3), 16)}, ${parseInt(
				hoverBorderColor.slice(3, 5),
				16,
		  )}, ${parseInt(hoverBorderColor.slice(5, 7), 16)}, ${hoverBorderOpacity ?? 1})`
		: hoverBorderColor;
	const border = `${borderWidth ? `${borderWidth}px` : '0px'} ${(borderType ?? "").toLowerCase()} ${borderColorWithOpacity}`.trim();
	const hoverBorder = `${`${hoverBorderWidth ?? borderWidth ?? 0}px`} ${(hoverBorderType ?? borderType ?? "").toLowerCase()} ${hoverBorderColorWithOpacity ?? borderColorWithOpacity}`.trim();
	return {
		border,
		hoverBorder,
	};
}